import React, { useRef, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadCloud, Download, FileSpreadsheet, FileArchive, CheckCircle2, XCircle, RotateCcw,
         Sparkles, Loader2 } from "lucide-react";
import { PROFILO_DIPENDENTI } from "@/lib/profiliImport";

/**
 * L'import in blocco dei documenti di un catalogo.
 *
 * La finestra e' una sola per tutti i cataloghi - dipendenti, mezzi, e quelli
 * che verranno - perche' il modo di lavorare e' lo stesso: analizza l'archivio,
 * completa le righe in giallo, ricarica. Quel che cambia da un catalogo
 * all'altro sta in `lib/profiliImport.js`: indirizzi, etichette e come si
 * chiama chi possiede il documento.
 */
export default function ImportDocumentiDialog({ profilo = PROFILO_DIPENDENTI, onClose, onDone }) {
  const zipRef = useRef(null);
  const xlsRef = useRef(null);
  const [zip, setZip] = useState(null);
  const [xls, setXls] = useState(null);
  const [tipo, setTipo] = useState(profilo.tipoIniziale);
  const [archivePrev, setArchivePrev] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const [analizzando, setAnalizzando] = useState(false);

  // Scarica un file dal server mostrando il motivo quando non viene: il
  // messaggio dice quale documento o quale limite ha fermato il lavoro, e
  // nasconderlo dietro un errore generico costa solo tempo a chi deve capire
  // cosa correggere.
  const scarica = async (indirizzo, nomeFile, opzioni) => {
    const r = await authFetch(indirizzo, opzioni);
    if (!r.ok) {
      let dettaglio = `errore ${r.status}`;
      try {
        const j = await r.json();
        if (j?.detail) dettaglio = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
      } catch { /* risposta non in formato JSON: resta il codice di stato */ }
      throw new Error(dettaglio);
    }
    const b = await r.blob();
    const u = window.URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = u;
    a.download = nomeFile;
    a.click();
    window.URL.revokeObjectURL(u);
  };

  // Legge l'archivio e restituisce il modello gia' compilato: e' il modo piu'
  // rapido di partire, perche' evita di ridigitare cio' che sta gia' scritto
  // nei documenti e nei nomi dei file.
  const analizzaZip = async () => {
    if (!zip) { toast.error("Seleziona prima l'archivio ZIP"); return; }
    setAnalizzando(true);
    try {
      const fd = new FormData();
      fd.append("zip_file", zip);
      await scarica(profilo.analisiUrl, profilo.analisiFile, { method: "POST", body: fd });
      toast.success("Analisi completata: apri il file e completa le righe in giallo");
    } catch (e) { toast.error(`Analisi non riuscita · ${e.message || "errore sconosciuto"}`); }
    finally { setAnalizzando(false); }
  };

  const downloadTemplate = async () => {
    try {
      await scarica(profilo.modelloUrl, profilo.modelloFile);
      toast.success("Modello scaricato");
    } catch { toast.error("Errore nel download del modello"); }
  };

  const submit = async () => {
    if (!zip) { toast.error("Seleziona l'archivio ZIP"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("zip_file", zip);
    if (xls) fd.append("excel_file", xls);
    fd.append(profilo.campoTipo, tipo);
    fd.append("archive_previous", archivePrev ? "true" : "false");
    try {
      const { data } = await api.post(profilo.importUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(data);
      if (data.imported > 0) toast.success(`${data.imported} documenti importati`);
      else toast.warning("Nessun documento importato — controlla il report");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'importazione");
    } finally { setBusy(false); }
  };

  const downloadReportCsv = () => {
    if (!report) return;
    const campi = ["file", "status", ...(profilo.campiCsv || []), "reason"];
    const head = campi.join(";") + "\n";
    const body = report.details.map(d =>
      campi.map(k => String(d[k] ?? "").replace(/;/g, ",")).join(";")
    ).join("\n");
    const blob = new Blob(["﻿" + head + body], { type: "text/csv;charset=utf-8;" });
    const u = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = `report_import_${profilo.codice}_${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(u);
  };

  const reset = () => { setReport(null); setZip(null); setXls(null); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{profilo.titolo}</DialogTitle>
        </DialogHeader>

        {!report && (
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-2">
              <div className="text-sm font-semibold">1 · Scarica e compila il modello</div>
              <p className="text-xs text-slate-600">{profilo.spiegaModello}</p>
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="w-3 h-3 mr-1" /> Scarica modello .xlsx
              </Button>
              {profilo.notaModello && (
                <p className="text-[11px] text-slate-500">{profilo.notaModello}</p>
              )}
            </div>

            <div className="p-3 bg-orange-50 border border-orange-200 rounded space-y-2">
              <div className="text-sm font-semibold">Scorciatoia · Fatti compilare il modello</div>
              <p className="text-xs text-slate-600">
                Seleziona l&apos;archivio qui sotto e premi Analizza: il sistema legge i documenti e i
                nomi dei file e ti restituisce il modello <strong>già compilato</strong>, con in
                giallo le sole righe da completare. Non scrive nulla in archivio.
              </p>
              <Button variant="outline" size="sm" onClick={analizzaZip}
                      disabled={analizzando || !zip} data-testid="analizza-zip-btn">
                {analizzando
                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Analisi in corso...</>
                  : <><Sparkles className="w-3 h-3 mr-1" /> Analizza l&apos;archivio</>}
              </Button>
              {!zip && <p className="text-[11px] text-amber-700">Seleziona prima l&apos;archivio ZIP qui sotto.</p>}
            </div>

            <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-2">
              <div className="text-sm font-semibold">2 · Prepara l&apos;archivio ZIP</div>
              <p className="text-xs text-slate-600">{profilo.spiegaZip}</p>
              <div className="p-2 bg-white rounded font-mono text-[11px] border border-slate-200">
                {profilo.esempioZip.map((riga, i) => (
                  <div key={i} style={{ whiteSpace: "pre" }}>{riga}</div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Archivio ZIP *</Label>
                <input ref={zipRef} type="file" accept=".zip" hidden
                       onChange={(e) => setZip(e.target.files?.[0] || null)} />
                <Button variant="outline" className="w-full mt-1 justify-start"
                        onClick={() => zipRef.current?.click()}>
                  <FileArchive className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{zip ? zip.name : "Seleziona ZIP..."}</span>
                </Button>
              </div>
              <div>
                <Label>File dati .xlsx (consigliato)</Label>
                <input ref={xlsRef} type="file" accept=".xlsx,.xlsm" hidden
                       onChange={(e) => setXls(e.target.files?.[0] || null)} />
                <Button variant="outline" className="w-full mt-1 justify-start"
                        onClick={() => xlsRef.current?.click()}>
                  <FileSpreadsheet className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{xls ? xls.name : "Seleziona Excel..."}</span>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>{profilo.tipoEtichetta}</Label>
                <select className="w-full h-10 border border-slate-200 rounded px-3 mt-1"
                        value={tipo} onChange={(e) => setTipo(e.target.value)}
                        data-testid="tipo-predefinito">
                  {profilo.tipoOpzioni.map((o) => (
                    <option key={o.valore} value={o.valore}>{o.nome}</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 mt-1">{profilo.tipoNota}</p>
              </div>
              <div className="flex items-start gap-2 pt-6">
                <input id="archprev" type="checkbox" className="mt-1"
                       checked={archivePrev} onChange={(e) => setArchivePrev(e.target.checked)} />
                <label htmlFor="archprev" className="text-xs text-slate-600">
                  {profilo.archiviaEtichetta}
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button onClick={submit} disabled={busy || !zip}
                      data-testid="avvia-import-btn"
                      className="bg-orange-500 hover:bg-orange-600 text-white">
                <UploadCloud className="w-4 h-4 mr-2" />
                {busy ? "Importazione in corso..." : "Avvia importazione"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {report && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Importati" value={report.imported} tone="ok" />
              <Stat label="Scartati" value={report.skipped} tone={report.skipped ? "warn" : "neutral"} />
              <Stat label={profilo.contatoreSoggetti.nome}
                    value={report[profilo.contatoreSoggetti.chiave]} tone="neutral" />
              <Stat label="Archiviati" value={report.archived_previous} tone="neutral" />
            </div>

            {report.skipped > 0 && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                {profilo.avvisoScarti}
              </p>
            )}

            <div className="border border-slate-200 rounded overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-xs min-w-[640px]">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                  <tr>
                    <th className="text-left p-2">File</th>
                    <th className="text-left p-2">Esito</th>
                    {profilo.colonneReport.map((c) => (
                      <th key={c.nome} className="text-left p-2">{c.nome}</th>
                    ))}
                    <th className="text-left p-2">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {report.details.map((d, i) => (
                    <tr key={i} className={`border-t border-slate-100 ${d.status === "scartato" ? "bg-red-50/50" : ""}`}>
                      <td className="p-2 font-mono truncate max-w-[180px]" title={d.file}>{d.file}</td>
                      <td className="p-2">
                        {d.status === "importato"
                          ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">
                              <CheckCircle2 className="w-3 h-3 mr-1" />importato</Badge>
                          : <Badge variant="destructive" className="text-[10px]">
                              <XCircle className="w-3 h-3 mr-1" />scartato</Badge>}
                      </td>
                      {profilo.colonneReport.map((c) => (
                        <td key={c.nome} className="p-2">{c.valore(d) || "—"}</td>
                      ))}
                      <td className="p-2 text-slate-500">{d.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={downloadReportCsv}>
                <Download className="w-4 h-4 mr-2" /> Scarica report CSV
              </Button>
              <Button variant="outline" onClick={reset}>
                <RotateCcw className="w-4 h-4 mr-2" /> Nuovo import
              </Button>
              <Button onClick={onClose} className="bg-orange-500 hover:bg-orange-600 text-white">
                Chiudi
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    ok: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warn: "border-amber-200 bg-amber-50 text-amber-700",
    neutral: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div className={`rounded border p-3 ${tones[tone] || tones.neutral}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-display text-2xl font-black">{value ?? 0}</div>
    </div>
  );
}
